import React, { useEffect, useState, useRef } from 'react';
import { CHARACTERS } from '../logic/gameData';
import gsap from 'gsap';
import { FallingLeaves, MotionLines, Flash, Vignette, Overlay } from '../common/Effects';
import { AnimatedBackground } from '../common/AnimatedBackgrounds';

export default function Story({ gameLogic, onLeaveStory }) {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [location, setLocation] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentEffects, setCurrentEffects] = useState({
    fallingLeaves: null,
    motionLines: null,
    screenShake: null,
    flash: null,
    vignette: null,
    overlay: null
  });
  
  const locationRef = useRef();
  const characterRefs = useRef({});
  const dialogRef = useRef();
  const choicesRef = useRef();
  const containerRef = useRef();
  const backgroundRef = useRef();
  const effectRefs = useRef({});
  const typingTimeoutRef = useRef(null);

  const cleanupScene = () => {
    gsap.killTweensOf(locationRef.current);
    gsap.killTweensOf(containerRef.current);
    gsap.killTweensOf(dialogRef.current);
    Object.values(characterRefs.current).forEach(ref => gsap.killTweensOf(ref));
    Object.values(effectRefs.current).forEach(ref => gsap.killTweensOf(ref));
    if (backgroundRef.current?.imageRef) {
      gsap.killTweensOf(backgroundRef.current.imageRef);
    }

    setLocation(null);
    setCharacters([]);
    setCurrentSpeaker(null);
    setDialogVisible(false);
    setCurrentEffects({
      fallingLeaves: null,
      motionLines: null,
      screenShake: null,
      flash: null,
      vignette: null,
      overlay: null
    });
  };

  useEffect(() => {
    const current = gameLogic.getCurrentContent();
    setCurrentEvent(current.event);

    return () => {
      cleanupScene();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!currentEvent) return;

    switch (currentEvent.type) {
      case 'showLocation':
        if (location !== currentEvent.locationId) {
          setLocation(currentEvent.locationId);
          gsap.fromTo(locationRef.current,
            { opacity: 0 },
            { opacity: 1, duration: 0.3, onComplete: handleAdvance }
          );
        } else {
          handleAdvance();
        }
        break;

      case 'hideLocation':
        gsap.to(locationRef.current,
          { 
            opacity: 0, 
            duration: 0.3,
            onComplete: () => {
              setLocation(null);
              handleAdvance();
            }
          }
        );
        break;

      case 'showCharacter':
        const newCharacter = {
          id: currentEvent.characterId,
          variation: currentEvent.variation,
          position: currentEvent.position
        };

        let existingCharacter = characters.find(char =>
          char.id === currentEvent.characterId &&
          char.position === currentEvent.position
        );

        if (existingCharacter) {
          setCharacters(prev => {
            const filtered = prev.filter(char =>
              char.id !== newCharacter.id && char.position !== newCharacter.position
            );
            return [...filtered, newCharacter];
          });
          handleAdvance();
          break;
        }

        const timeline = gsap.timeline({
          onComplete: handleAdvance
        });

        existingCharacter = characters.find(char => char.id === currentEvent.characterId);
        if (existingCharacter) {
          timeline.to(characterRefs.current[existingCharacter.position],
            { opacity: 0, duration: 0.3 }
          );
        }

        const characterAtPosition = characters.find(char =>
          char.position === currentEvent.position && char.id !== currentEvent.characterId
        );
        if (characterAtPosition) {
          timeline.to(characterRefs.current[characterAtPosition.position],
            { opacity: 0, duration: 0.3 },
            existingCharacter ? ">" : 0
          );
        }

        setCharacters(prev => {
          const filtered = prev.filter(char =>
            char.id !== newCharacter.id && char.position !== newCharacter.position
          );
          return [...filtered, newCharacter];
        });

        timeline.fromTo(characterRefs.current[newCharacter.position],
          { opacity: 0 },
          { opacity: 1, duration: 0.5 },
          ">"
        );
        break;

      case 'hideCharacter':
        setCharacters(prev => prev.filter(char =>
          !(char.id === currentEvent.characterId && char.position === currentEvent.position)
        ));

        if (characterRefs.current[currentEvent.position]?.id === currentEvent.characterId) {
          gsap.to(characterRefs.current[currentEvent.position],
            {
              opacity: 0,
              duration: 0.5,
              onComplete: handleAdvance
            }
          );
        } else {
          handleAdvance();
        }
        break;

      case 'showDialog':
      case 'showNarration':
        setCurrentSpeaker(currentEvent.type === 'showDialog' ? currentEvent.speaker : null);

        if (currentEvent.type === 'showDialog' && currentEvent.variation) {
          setCharacters(prev => prev.map(char =>
            char.id === currentEvent.speaker
              ? { ...char, variation: currentEvent.variation }
              : char
          ));
        }

        if (!dialogVisible) {
          gsap.fromTo(dialogRef.current,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.15 }
          );
        }
        setDialogVisible(true);
        setTypedText('');
        setIsTyping(true);

        if (gameLogic.getToTypeText()) {
          let currentText = '';
          const fullText = currentEvent.text;
          const interval = 1000 / gameLogic.getTypingSpeed();
          let charIndex = 0;

          const typeChar = () => {
            if (charIndex < fullText.length) {
              currentText += fullText[charIndex];
              setTypedText(currentText);
              charIndex++;
              typingTimeoutRef.current = setTimeout(typeChar, interval);
            } else {
              setIsTyping(false);
            }
          };

          typeChar();
        } else {
          setTypedText(currentEvent.text);
          setIsTyping(false);
        }
        break;

      case 'showEffect':
        if (currentEvent.effect === 'screenShake') {
          const intensity = currentEvent.intensity || 10;
          const duration = currentEvent.duration || 0.5;
          const shakeTimeline = gsap.timeline();

          for (let i = 0; i < duration * 10; i++) {
            shakeTimeline.to(containerRef.current, {
              x: Math.random() * intensity - intensity/2,
              y: Math.random() * intensity - intensity/2,
              duration: 0.05
            });
          }

          shakeTimeline.to(containerRef.current, {
            x: 0,
            y: 0,
            duration: 0.1,
            onComplete: () => {
              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }
          });

          if (currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        } else if (currentEvent.effect === 'movement') {
          gsap.to(backgroundRef.current.imageRef, {
            x: `${currentEvent.x}%`,
            duration: currentEvent.duration || 1,
            ease: 'power1.inOut',
            onComplete: () => {
              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }
          });

          if (currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        } else if (currentEvent.effect === 'zoom') {
          gsap.to(backgroundRef.current.imageRef, {
            scale: currentEvent.scale || 1,
            duration: currentEvent.duration || 1,
            ease: 'power1.inOut',
            onComplete: () => {
              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }
          });

          if (currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        } else if (currentEvent) {
          setCurrentEffects(prev => ({
            ...prev,
            [currentEvent.effect]: {
              ...currentEvent
            }
          }));

          if (currentEvent.duration) {
            setTimeout(() => {
              setCurrentEffects(prev => ({
                ...prev,
                [currentEvent.effect]: null
              }));

              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }, currentEvent.duration * 1000);
          }

          if (!currentEvent.duration || currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        }
        break;

      case 'hideEffect':
        if (effectRefs.current[currentEvent.effect]) {
          gsap.to(effectRefs.current[currentEvent.effect], {
            opacity: 0,
            duration: currentEvent.fadeOutDuration || 0.3,
            onComplete: () => {
              setCurrentEffects(prev => ({
                ...prev,
                [currentEvent.effect]: null
              }));
              handleAdvance();
            }
          });
        } else {
          setCurrentEffects(prev => ({
            ...prev,
            [currentEvent.effect]: null
          }));
          handleAdvance();
        }
        break;

      default:
        throw new Error(`Unrecognized event type: ${currentEvent.type}`);
    }
  }, [currentEvent]);

  const handleAdvance = () => {
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setTypedText(currentEvent.text);
      setIsTyping(false);
      return;
    }

    const currentChapter = gameLogic.position.chapter;
    const result = gameLogic.advance();

    if (result.isGameOver) {
      onLeaveStory();
      return;
    }

    const current = gameLogic.getCurrentContent();
    const nextEventType = current.event.type;

    if (gameLogic.position.chapter !== currentChapter) {
      cleanupScene();
    }

    if (!['showDialog', 'showNarration'].includes(nextEventType)) {
      setDialogVisible(false);
      setCurrentSpeaker(null);
    }

    setCurrentEvent(current.event);
  };

  const handleChoice = (index) => {
    const currentChapter = gameLogic.position.chapter;
    const result = gameLogic.makeChoice(index);

    if (result.isGameOver) {
      onLeaveStory();
      return;
    }

    const current = gameLogic.getCurrentContent();
    const nextEventType = current.event.type;

    if (gameLogic.position.chapter !== currentChapter) {
      cleanupScene();
    }

    if (!['showDialog', 'showNarration'].includes(nextEventType)) {
      setDialogVisible(false);
      setCurrentSpeaker(null);
    }

    setCurrentEvent(current.event);
  };

  const handleDialogClick = (e) => {
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setTypedText(currentEvent.text);
      setIsTyping(false);
    } else if (!currentEvent.choices) {
      handleAdvance();
    }
  };

  if (!currentEvent) return null;

  const positions = ['left', 'center', 'right'];
  const getCharacterPosition = (position) => {
    switch (position) {
      case 'left': return 'left-1/4';
      case 'right': return 'left-3/4';
      default: return 'left-1/2';
    }
  };

  return (
    <div className="fixed inset-0 w-screen h-[100svh] overflow-hidden bg-[#1a1a2e]" ref={containerRef}>
      <div className="w-screen h-[100svh] relative">
        {/* Background Layer - z-0 */}
        <div className="absolute inset-0 z-[0] pointer-events-none" ref={locationRef}>
          {location && (
            <>
              <AnimatedBackground
                ref={backgroundRef}
                imageSrc={gameLogic.getSpriteUrl(location)}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1a1a2e]/20 to-[#1a1a2e]/60" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(246,211,101,0.08),transparent_70%)]" />
            </>
          )}
        </div>

        {/* Atmospheric Layer - z-10 */}
        <div className="absolute inset-0 z-[10] pointer-events-none">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#F6D365]/10 rounded-full filter blur-[180px] animate-float-slow" />
          <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-[#7C9CBC]/10 rounded-full filter blur-[180px] animate-float-delayed" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/30 via-transparent to-transparent" />
        </div>

        {/* Character Layer - z-20/30 */}
        {positions.map(position => (
          <div 
            key={position}
            className={`absolute bottom-[-50px] ${getCharacterPosition(position)} transform -translate-x-1/2 pointer-events-none`}
            ref={el => characterRefs.current[position] = el}
            style={{
              width: '416px', 
              height: '624px',
              zIndex: currentSpeaker === (characters.find(c => c.position === position)?.id) ? 30 : 20
            }}
          >
            {characters.map(char => {
              if (char.position === position) {
                return (
                  <img
                    key={char.id}
                    src={gameLogic.getSpriteUrl(char.id, char.variation)}
                    alt={CHARACTERS[char.id].name}
                    className="transition-all duration-700"
                    style={{
                      filter: currentSpeaker ? (
                        currentSpeaker === char.id 
                          ? 'brightness(1.15) drop-shadow(0 0 20px rgba(246,211,101,0.2))' 
                          : 'brightness(0.4) saturate(0.7)'
                      ) : 'brightness(1)'
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
        ))}

        {/* Effects Layer - z-35 */}
        <div className="absolute inset-0 z-[35] pointer-events-none">
          {currentEffects.fallingLeaves && (
            <FallingLeaves 
              color={currentEffects.fallingLeaves.color}
              count={currentEffects.fallingLeaves.count}
            />
          )}
          {currentEffects.motionLines && (
            <MotionLines 
              duration={currentEffects.motionLines.duration} 
              rotation={currentEffects.motionLines.rotation} 
            />
          )}
          {currentEffects.flash && (
            <Flash 
              color={currentEffects.flash.color}
              duration={currentEffects.flash.duration}
            />
          )}
          {currentEffects.vignette && (
            <Vignette 
              intensity={currentEffects.vignette.intensity}
            />
          )}
          {currentEffects.overlay && (
            <div ref={el => effectRefs.current['overlay'] = el}>
              <Overlay 
                overlayType={currentEffects.overlay.overlayType}
                opacity={currentEffects.overlay.opacity}
                gameLogic={gameLogic}
              />
            </div>
          )}
        </div>

        {/* Dialog Layer - z-40 */}
        {(currentEvent.type === 'showDialog' || currentEvent.type === 'showNarration') && (
          <div 
            className="absolute bottom-0 left-0 w-full p-4 cursor-pointer z-[40]" 
            ref={dialogRef} 
            onClick={handleDialogClick}
          >
            <div className="relative backdrop-blur-md bg-gradient-to-br from-[#1a1a2e]/85 to-[#16213E]/85 
                          rounded-2xl p-6 text-white/90 
                          border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]
                          transition-all duration-300">
              <div className="absolute -top-0.5 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#F6D365]/30 to-transparent" />
              <div className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#7C9CBC]/30 to-transparent" />
              
              {currentEvent.type === 'showDialog' && (
                <div className="mb-3 font-bold text-xl font-['Montserrat']">
                  <span className="bg-gradient-to-r from-[#F6D365] to-[#FDA085] bg-clip-text text-transparent">
                    {CHARACTERS[currentEvent.speaker].name}
                  </span>
                </div>
              )}

              <div className={`mb-4 text-lg leading-relaxed
                            ${currentEvent.type === 'showNarration' 
                              ? 'font-["Source_Sans_Pro"] italic text-[#F6D365]/90' 
                              : 'font-["Lato"] text-white/90'}`}>
                {typedText}
              </div>

              {!isTyping && !currentEvent.choices && (
                <div className="absolute bottom-6 right-6">
                  <div className="animate-bounce-subtle text-[#F6D365] hover:text-white 
                                transition-colors duration-300 text-2xl">
                    →
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Choices Layer - z-50 */}
        {!isTyping && currentEvent.choices && (
          <div className="absolute inset-0 flex items-center justify-center z-[50]">
            <div className="space-y-4 transform transition-all duration-300 opacity-0 animate-fade-in w-full max-w-[600px] px-4">
              {currentEvent.choices.map((choice, index) => (
                <button
                  key={index}
                  onClick={() => handleChoice(index)}
                  className="w-full bg-gradient-to-r from-[#1a1a2e]/90 to-[#16213E]/90 
                           hover:from-[#F6D365]/20 hover:to-[#7C9CBC]/20
                           backdrop-blur-sm border border-white/10 
                           py-4 px-8 rounded-xl text-white/90 text-left
                           font-['Source_Sans_Pro'] text-lg
                           shadow-[0_4px_15px_rgba(246,211,101,0.1)]
                           transform transition-all duration-300 
                           hover:shadow-[0_4px_20px_rgba(246,211,101,0.2)]
                           hover:border-[#F6D365]/30 hover:text-white
                           active:scale-[0.98]"
                >
                  {choice.text}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          50% { transform: translate(-30px, 30px); opacity: 0.1; }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translate(0, 0); opacity: 0.2; }
          50% { transform: translate(30px, -30px); opacity: 0.05; }
        }
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-float-slow {
          animation: float-slow 12s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 15s ease-in-out infinite;
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 1s infinite;
        }
        .animate-fade-in {
          animation: fade-in 0.3s forwards;
        }
      `}</style>
    </div>
  );
}
